/**
 * Gesture Handler stub for web — enough for Clickable + GestureHandlerRootView.
 * Native package is not loaded on PWA (Vite alias).
 *
 * Gesture builders are Proxy-based so Pan/Tap chains (minDistance, onChange, …)
 * never throw — jelly cards / carousels still mount; motion is a no-op on web.
 *
 * Detector uses a DOM `onClick` host (not RN Pressable): Pressable inside FlatList
 * often drops presses on RN-web, which broke menu select/deselect.
 */
import React from 'react';
import {
  FlatList as RNFlatList,
  ScrollView as RNScrollView,
  StyleSheet,
  View,
  type StyleProp,
  type ViewProps,
  type ViewStyle,
} from 'react-native';

export const FlatList = RNFlatList;
export const ScrollView = RNScrollView;

type Handler = {
  onBegin?: (e: unknown) => void;
  onStart?: (e: unknown) => void;
  onUpdate?: (e: unknown) => void;
  onChange?: (e: unknown) => void;
  onEnd?: (e: unknown, success?: boolean) => void;
  onFinalize?: (e: unknown, success?: boolean) => void;
  onTouchesDown?: (e: unknown) => void;
  onTouchesMove?: (e: unknown) => void;
  onTouchesUp?: (e: unknown) => void;
};

const HANDLER_KEYS = new Set([
  'onBegin',
  'onStart',
  'onUpdate',
  'onChange',
  'onEnd',
  'onFinalize',
  'onTouchesDown',
  'onTouchesMove',
  'onTouchesUp',
]);

function makeChainable(kind: string) {
  const state: Handler & {
    _kind: string;
    [key: string]: unknown;
  } = {
    _kind: kind,
  };

  const api: Record<string, unknown> = {
    __config: state,
  };

  return new Proxy(api, {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') {
        return Reflect.get(target, prop, receiver);
      }
      if (prop in target) {
        return target[prop];
      }
      // Any RNGH builder method (.minDistance, .activeOffsetY, .enabled, …) chains.
      return (...args: unknown[]) => {
        if (HANDLER_KEYS.has(prop) && typeof args[0] === 'function') {
          (state as Record<string, unknown>)[prop] = args[0];
          // onChange is the Gesture API name; keep onUpdate in sync for Detector.
          if (prop === 'onChange' && state.onUpdate == null) {
            state.onUpdate = args[0] as Handler['onUpdate'];
          }
        } else if (args.length > 0) {
          state[`_${prop}`] = args.length === 1 ? args[0] : args;
        }
        return receiver;
      };
    },
  });
}

export const Gesture = {
  Tap: () => makeChainable('tap'),
  Pan: () => makeChainable('pan'),
  LongPress: () => makeChainable('longPress'),
  Pinch: () => makeChainable('pinch'),
  Rotation: () => makeChainable('rotation'),
  Fling: () => makeChainable('fling'),
  Native: () => makeChainable('native'),
  Exclusive: (...gestures: Array<{ __config?: Handler & { _kind?: string } }>) => {
    const tap = gestures.find(g => g?.__config?._kind === 'tap');
    return tap ?? gestures.find(g => g?.__config) ?? makeChainable('exclusive');
  },
  Simultaneous: (...gestures: Array<{ __config?: Handler & { _kind?: string } }>) => {
    // Prefer Tap so jelly cards keep quantity onEnd (not the pan follower).
    const tap = gestures.find(g => g?.__config?._kind === 'tap');
    return tap ?? gestures.find(g => g?.__config) ?? makeChainable('simultaneous');
  },
  Race: (...gestures: Array<{ __config?: Handler & { _kind?: string } }>) => {
    const tap = gestures.find(g => g?.__config?._kind === 'tap');
    return tap ?? gestures.find(g => g?.__config) ?? makeChainable('race');
  },
};

type DetectorProps = ViewProps & {
  gesture?: { __config?: Handler & { _hitSlop?: unknown; _kind?: string }; onEnd?: Handler['onEnd'] };
  children?: React.ReactNode;
};

/**
 * Nested GestureDetectors (card tap wrapping +/- steppers) both receive click on
 * RN-web. Inner marks this lock so the outer card does not re-toggle quantity.
 */
let nested_gesture_press_lock = false;

function readClickPoint(
  e: {
    clientX?: number;
    clientY?: number;
    currentTarget?: { getBoundingClientRect?: () => DOMRect };
    nativeEvent?: {
      locationX?: number;
      locationY?: number;
      offsetX?: number;
      offsetY?: number;
      clientX?: number;
      clientY?: number;
    };
  },
): { x: number; y: number } {
  const ne = e?.nativeEvent;
  if (typeof ne?.locationX === 'number' && typeof ne?.locationY === 'number') {
    return { x: ne.locationX, y: ne.locationY };
  }
  if (typeof ne?.offsetX === 'number' && typeof ne?.offsetY === 'number') {
    return { x: ne.offsetX, y: ne.offsetY };
  }
  const target = e?.currentTarget;
  const rect = typeof target?.getBoundingClientRect === 'function' ? target.getBoundingClientRect() : null;
  const client_x = typeof e?.clientX === 'number' ? e.clientX : typeof ne?.clientX === 'number' ? ne.clientX : 0;
  const client_y = typeof e?.clientY === 'number' ? e.clientY : typeof ne?.clientY === 'number' ? ne.clientY : 0;
  if (rect) {
    return { x: client_x - rect.left, y: client_y - rect.top };
  }
  return { x: 0, y: 0 };
}

function hitSlopToMargin(
  hit_slop: number | { top?: number; bottom?: number; left?: number; right?: number } | undefined,
): ViewStyle | undefined {
  if (hit_slop == null) {
    return undefined;
  }
  if (typeof hit_slop === 'number') {
    return { margin: -hit_slop };
  }
  return {
    marginTop: hit_slop.top != null ? -hit_slop.top : undefined,
    marginBottom: hit_slop.bottom != null ? -hit_slop.bottom : undefined,
    marginLeft: hit_slop.left != null ? -hit_slop.left : undefined,
    marginRight: hit_slop.right != null ? -hit_slop.right : undefined,
  };
}

export function GestureDetector({ gesture, children, style, ...rest }: DetectorProps) {
  const cfg = (gesture as { __config?: Handler & { _hitSlop?: unknown } } | undefined)?.__config;
  const hit_slop = cfg?._hitSlop as
    | number
    | { top?: number; bottom?: number; left?: number; right?: number }
    | undefined;
  const hit_margin = hitSlopToMargin(hit_slop);

  const fire = (success: boolean, point: { x: number; y: number }) => {
    const evt = { success, x: point.x, y: point.y };
    // Isolate handler failures so onEnd still runs (quantity / Clickable press).
    try {
      cfg?.onBegin?.(evt);
    } catch {
      // ignore
    }
    try {
      cfg?.onStart?.(evt);
    } catch {
      // ignore
    }
    try {
      // RNGH Tap.onEnd(event, success) — Clickable returns early when success is falsy.
      cfg?.onEnd?.(evt, success);
    } catch {
      // ignore
    }
    try {
      cfg?.onFinalize?.(evt, success);
    } catch {
      // ignore
    }
  };

  const host_style: StyleProp<ViewStyle> = StyleSheet.flatten([
    style,
    hit_margin,
    // Ensure the host receives clicks even when children are non-pressable Views.
    { cursor: 'pointer' } as ViewStyle,
  ]);

  return (
    <View
      {...rest}
      style={host_style}
      // RN-web: View → div; onClick is reliable inside ScrollView/FlatList (Pressable often is not).
      // @ts-expect-error RN-web DOM click
      onClick={(e: {
        stopPropagation?: () => void;
        clientX?: number;
        clientY?: number;
        currentTarget?: { getBoundingClientRect?: () => DOMRect };
        nativeEvent?: { stopPropagation?: () => void; stopImmediatePropagation?: () => void };
      }) => {
        e?.stopPropagation?.();
        e?.nativeEvent?.stopPropagation?.();
        e?.nativeEvent?.stopImmediatePropagation?.();
        if (nested_gesture_press_lock) {
          return;
        }
        nested_gesture_press_lock = true;
        queueMicrotask(() => {
          nested_gesture_press_lock = false;
        });
        fire(true, readClickPoint(e));
      }}
    >
      {children}
    </View>
  );
}

export function GestureHandlerRootView({
  children,
  style,
  ...rest
}: ViewProps & { children?: React.ReactNode }) {
  return (
    <View style={[{ flex: 1 }, style]} {...rest}>
      {children}
    </View>
  );
}

export const State = {
  UNDETERMINED: 0,
  FAILED: 1,
  BEGAN: 2,
  CANCELLED: 3,
  ACTIVE: 4,
  END: 5,
};

export const Directions = {
  RIGHT: 1,
  LEFT: 2,
  UP: 4,
  DOWN: 8,
};

export default {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
  FlatList,
  ScrollView,
  State,
  Directions,
};
